/**
 * EditableBox - Real-time collaborative editing component for BOB Analysis sections
 *
 * Design principles (from realtime-collaboration-design.md):
 * 1. Optimistic Updates: Broadcast changes IMMEDIATELY via WebSocket
 * 2. Field-Level Locking: When another user focuses this field, it becomes readonly
 * 3. Self-Update Filtering: Skip WebSocket updates from self (already have local state)
 * 4. Background Save: DB save happens after broadcast with debounce
 *
 * Flow:
 * - onFocus → acquire lock (broadcast start_editing_section)
 * - onChange → update local state → broadcast immediately → debounced save
 * - onBlur → release lock (broadcast stop_editing_section)
 * - WebSocket update → if not self and not this field's lock holder → apply update
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { websocketService, type BobContentUpdate } from '@/lib/services/websocketService'
import { useAuthStore } from '@/lib/stores/authStore'
import { Lock, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface EditableBoxProps {
  title: string
  values: string[]
  onSave: (values: string[]) => Promise<void>
  colorScheme: 'blue' | 'orange' | 'green' | 'neutral'
  registerSave?: (save: () => Promise<void>) => void
  registerGetValues?: (getValues: () => string[]) => void
  section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
  category: string
  onDelete?: () => void | Promise<void>
  isCustom?: boolean
}

// Debounce delays (in ms)
const BROADCAST_DEBOUNCE = 50 // Fast broadcast for responsiveness
const SAVE_DEBOUNCE = 500 // Slower save to reduce API calls
const BLUR_LOCK_RELEASE_DELAY = 300 // Delay before releasing lock on blur

// Convert bullet point list to HTML for the editor
const valuesToHtml = (vals: string[]): string => {
  return vals.length > 0 ? `<ul>${vals.map(v => `<li>${v}</li>`).join('')}</ul>` : ''
}

// Extract HTML content from list items and paragraphs, preserving formatting (bold, italic, etc.)
const extractValuesFromHtml = (html: string): string[] => {
  if (!html) return []

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  const extractedValues: string[] = []

  // First, try to extract from list items (preserving HTML formatting)
  const listItems = tempDiv.querySelectorAll('li')
  if (listItems.length > 0) {
    listItems.forEach(item => {
      // Use innerHTML to preserve formatting (bold, italic, etc.)
      const content = item.innerHTML?.trim()
      if (content) {
        extractedValues.push(content)
      }
    })
    return extractedValues // ONLY return list items if they exist
  }

  // If no list items, extract from paragraphs (when user exits bullet list with double-enter)
  const paragraphs = tempDiv.querySelectorAll('p')
  if (paragraphs.length > 0) {
    paragraphs.forEach(p => {
      const content = p.innerHTML?.trim()
      if (content) {
        extractedValues.push(content)
      }
    })
    return extractedValues // ONLY return paragraphs if they exist
  }

  // Fallback: if no structure found, get text content
  const text = tempDiv.textContent?.trim()
  if (text) {
    extractedValues.push(text)
  }

  return extractedValues
}

export function EditableBox({
  title,
  values,
  onSave,
  colorScheme,
  registerSave,
  registerGetValues,
  section,
  category,
  onDelete,
  isCustom = false,
}: EditableBoxProps) {

  // ============================================================================
  // State
  // ============================================================================

  // Content state - this is the source of truth for the editor
  const [htmlContent, setHtmlContent] = useState<string>(() => valuesToHtml(values))

  // Locking state - tracks if another user is editing this field
  const [isLockedByOther, setIsLockedByOther] = useState(false)
  const [lockingUserName, setLockingUserName] = useState<string | null>(null)

  // Save state
  const [isSaving, setIsSaving] = useState(false)

  // Track editing state for RichTextEditor (prevents cursor reset during typing)
  const [isEditing, setIsEditing] = useState(false)

  // Deletion confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Refs for cleanup and debouncing
  const broadcastTimeoutRef = useRef<number | null>(null)
  const saveTimeoutRef = useRef<number | null>(null)
  const blurTimeoutRef = useRef<number | null>(null)
  const isEditingRef = useRef(false)
  const lastSavedContentRef = useRef<string>(valuesToHtml(values))
  const currentHtmlRef = useRef<string>(valuesToHtml(values)) // Track current HTML without causing re-renders
  const isUpdatingFromPropRef = useRef(false) // Prevent onChange loop when setting content programmatically

  // Current user ID for filtering self-updates
  const currentUserId = useAuthStore.getState().user?.id

  // Section key for presence tracking
  const sectionKey = `${section}.${category}`

  // ============================================================================
  // WebSocket Subscription - Content Updates
  // ============================================================================

  useEffect(() => {
    const handleContentUpdate = (update: BobContentUpdate) => {
      // Self-update filtering: Skip updates from self (already have local state)
      if (update.user_id === currentUserId) {
        return
      }

      // Only apply updates for THIS specific section/category
      if (update.section !== section || update.category !== category) {
        return
      }

      // Apply the update from another user
      const newHtml = valuesToHtml(update.values)
      console.debug('[EditableBox] Applying WebSocket update', { section, category, valuesCount: update.values.length })

      isUpdatingFromPropRef.current = true
      setHtmlContent(newHtml)
      currentHtmlRef.current = newHtml
      lastSavedContentRef.current = newHtml

      // Reset flag after state update completes
      setTimeout(() => {
        isUpdatingFromPropRef.current = false
      }, 0)
    }

    const unsubscribe = websocketService.onContentUpdate(handleContentUpdate)
    return () => unsubscribe()
  }, [section, category, currentUserId])

  // ============================================================================
  // WebSocket Subscription - Field Locking
  // ============================================================================

  useEffect(() => {
    // Handle when another user starts editing a section
    const handleUserEditing = (
      userId: string,
      userName: string,
      editingSectionKey: string
    ) => {
      // If another user is editing THIS field, lock it
      if (userId !== currentUserId && editingSectionKey === sectionKey) {
        setIsLockedByOther(true)
        setLockingUserName(userName)
      }
    }

    // Handle when a user stops editing
    const handleUserStoppedEditing = (userId: string) => {
      // If the user who had the lock on our field stopped editing, unlock it
      if (userId !== currentUserId) {
        // We don't know which field they were editing, so check if it affects us
        // The safest approach: if any other user stops editing, and we were locked,
        // check if we should unlock. Backend tracks exact section, so we trust this.
        setIsLockedByOther(false)
        setLockingUserName(null)
      }
    }

    const unsubscribeEditing = websocketService.onUserEditingSection(handleUserEditing)
    const unsubscribeStopped = websocketService.onUserStoppedEditing(handleUserStoppedEditing)

    return () => {
      unsubscribeEditing()
      unsubscribeStopped()
    }
  }, [currentUserId, sectionKey])

  // ============================================================================
  // Update content when values prop changes (initial load or BOB switch)
  // ============================================================================

  useEffect(() => {
    // Only update from props if we're not currently editing
    if (!isEditingRef.current) {
      const newHtml = valuesToHtml(values)
      console.debug('[EditableBox] Updating from props', { section, category, valuesCount: values.length })

      isUpdatingFromPropRef.current = true
      setHtmlContent(newHtml)
      currentHtmlRef.current = newHtml
      lastSavedContentRef.current = newHtml

      // Reset flag after state update completes
      setTimeout(() => {
        isUpdatingFromPropRef.current = false
      }, 0)
    }
  }, [values, section, category])

  // ============================================================================
  // Exposed Functions (for parent component)
  // ============================================================================

  // Expose getCurrentValues function to parent
  // Use a ref to track if we've registered to avoid re-registering
  const hasRegisteredRef = useRef(false)

  useEffect(() => {
    if (registerGetValues && !hasRegisteredRef.current) {
      console.log('[EditableBox] Registering getValues function', { section, category })
      registerGetValues(() => {
        const values = extractValuesFromHtml(currentHtmlRef.current)
        console.log('[EditableBox] getValues called', { section, category, valuesCount: values.length })
        return values
      })
      hasRegisteredRef.current = true
    }
  }, [registerGetValues, section, category])

  // Reset hasRegistered when section or category changes (BOB switch or remount)
  useEffect(() => {
    hasRegisteredRef.current = false
  }, [section, category])

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleFocus = useCallback(() => {
    // Clear any pending blur timeout (user refocused quickly)
    if (blurTimeoutRef.current) {
      window.clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }

    isEditingRef.current = true
    setIsEditing(true)
    websocketService.startEditingSection(sectionKey)
  }, [sectionKey])

  const handleBlur = useCallback(() => {
    // Delay releasing the lock to avoid flickering when user quickly refocuses
    blurTimeoutRef.current = window.setTimeout(() => {
      isEditingRef.current = false
      setIsEditing(false)
      websocketService.stopEditingSection()

      // Normalize content back to bullet list format on blur
      // This ensures bullets are always shown after editing completes
      const currentHtml = currentHtmlRef.current
      const currentValues = extractValuesFromHtml(currentHtml)
      if (currentValues.length > 0) {
        const normalizedHtml = valuesToHtml(currentValues)
        // Only update if actually different and not empty
        if (normalizedHtml !== currentHtml && normalizedHtml !== '<ul></ul>') {
          console.debug('[EditableBox] Normalizing content on blur', { section, category })

          isUpdatingFromPropRef.current = true
          setHtmlContent(normalizedHtml)
          currentHtmlRef.current = normalizedHtml
          lastSavedContentRef.current = normalizedHtml

          // Reset flag after state update completes
          setTimeout(() => {
            isUpdatingFromPropRef.current = false
          }, 0)
        }
      }
    }, BLUR_LOCK_RELEASE_DELAY)
  }, [section, category])

  const handleContentChange = useCallback(
    (newHtml: string) => {
      // Skip if this change came from us setting the content programmatically
      if (isUpdatingFromPropRef.current) {
        console.debug('[EditableBox] Skipping handleContentChange - programmatic update')
        return
      }

      console.debug('[EditableBox] handleContentChange - user edit', { section, category, htmlLength: newHtml.length })

      // Clean up: Remove trailing empty paragraphs that appear after bullet lists
      // This prevents typing in non-bullet areas
      let cleanedHtml = newHtml
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = newHtml

      // Remove trailing empty <p> tags after <ul>
      const lastChild = tempDiv.lastElementChild
      if (lastChild && lastChild.tagName === 'P' && lastChild.textContent?.trim() === '') {
        lastChild.remove()
        cleanedHtml = tempDiv.innerHTML
      }

      // Update local state immediately
      setHtmlContent(cleanedHtml)
      currentHtmlRef.current = cleanedHtml // Keep ref in sync

      const newValues = extractValuesFromHtml(cleanedHtml)

      // Clear previous timeouts
      if (broadcastTimeoutRef.current) {
        window.clearTimeout(broadcastTimeoutRef.current)
      }
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current)
      }

      // Broadcast immediately (with minimal debounce for rapid typing)
      broadcastTimeoutRef.current = window.setTimeout(() => {
        websocketService.broadcastContentUpdate(section, category, newValues)
      }, BROADCAST_DEBOUNCE)

      // Save to database (with longer debounce, fire-and-forget)
      saveTimeoutRef.current = window.setTimeout(async () => {
        // Only save if content actually changed from last save
        if (cleanedHtml !== lastSavedContentRef.current) {
          setIsSaving(true)
          try {
            await onSave(newValues)
            lastSavedContentRef.current = cleanedHtml
          } catch (error) {
            console.error('[EditableBox] Save failed:', error)
          } finally {
            setIsSaving(false)
          }
        }
      }, SAVE_DEBOUNCE)
    },
    [section, category, onSave]
  )

  // ============================================================================
  // Register save function with parent (for external save triggers)
  // ============================================================================

  useEffect(() => {
    if (registerSave) {
      const saveWrapper = async () => {
        if (htmlContent !== lastSavedContentRef.current) {
          const newValues = extractValuesFromHtml(htmlContent)
          await onSave(newValues)
          lastSavedContentRef.current = htmlContent
        }
      }
      registerSave(saveWrapper)
    }
  }, [registerSave, htmlContent, onSave])

  // ============================================================================
  // Cleanup
  // ============================================================================

  useEffect(() => {
    return () => {
      if (broadcastTimeoutRef.current) window.clearTimeout(broadcastTimeoutRef.current)
      if (saveTimeoutRef.current) window.clearTimeout(saveTimeoutRef.current)
      if (blurTimeoutRef.current) window.clearTimeout(blurTimeoutRef.current)
    }
  }, [])

  // ============================================================================
  // Render
  // ============================================================================

  const colorStyles = {
    blue: { heading: 'text-blue-800' },
    orange: { heading: 'text-orange-800' },
    green: { heading: 'text-green-800' },
    neutral: { heading: 'text-gray-800' },
  }

  return (
    <div className="flex flex-col gap-3 relative">
      {/* Header with title, optional delete button, and save indicator */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className={cn('font-bold text-lg', colorStyles[colorScheme].heading)}>
          {title}
        </h3>

        <div className="flex items-center gap-2">
          {/* Delete button for custom sections */}
          {isCustom && onDelete && (
            <>
              {!showDeleteConfirm ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="Sectie verwijderen"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={async () => {
                    if (onDelete && !isDeleting) {
                      try {
                        setIsDeleting(true)
                        await onDelete()
                        setShowDeleteConfirm(false)
                      } catch (error) {
                        console.error('[EditableBox] Delete failed:', error)
                        setShowDeleteConfirm(false)
                      } finally {
                        setIsDeleting(false)
                      }
                    }
                  }}
                  className="h-7 px-3 text-xs"
                  onBlur={() => {
                    // Only cancel if not currently deleting
                    if (!isDeleting) {
                      setTimeout(() => setShowDeleteConfirm(false), 200)
                    }
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Bezig...' : 'Verwijderen?'}
                </Button>
              )}
            </>
          )}

          {/* Save indicator */}
          {isSaving && (
            <span className="text-xs text-muted-foreground animate-pulse">
              Opslaan...
            </span>
          )}
        </div>
      </div>

      {/* Rich text editor */}
      <RichTextEditor
        value={htmlContent}
        onChange={handleContentChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={isLockedByOther || isSaving}
        hideButtons={true}
        forceBulletList={true}
        isEditing={isEditing}
        className={cn(
          isLockedByOther && 'pointer-events-none border-orange-300'
        )}
      />

      {/* Message below field when another user is editing */}
      {isLockedByOther && (
        <div className="flex items-center gap-1.5">
          <Lock className="w-3 h-3 text-orange-500 flex-shrink-0" />
          <span className="text-xs text-orange-600">
            {lockingUserName || 'Iemand'} is dit veld aan het bewerken
          </span>
        </div>
      )}
    </div>
  )
}
